/**
 * SignUp Page Component
 *
 * A comprehensive sign-up page that matches the existing design system
 * and integrates with the backend signup API flow.
 */

import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTypographyHelpers } from "../../contexts/DesignContext";
import { APP_CONFIG } from "../../configs/config";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import SectionWrapper from "../ui/SectionWrapper";
import { useSignUpForm } from "./signup/useSignUpForm";
import SignUpFormFields from "./signup/SignUpFormFields";
import SignUpFormActions from "./signup/SignUpFormActions";
import {
  createHandleGetOtp,
  createHandleSubmit,
  createHandleImageChange,
} from "./signup/signUpHandlers";

const SignUpPage = () => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const { getPrimaryHeading, getSupportingText } = useTypographyHelpers();

  // Use custom hook for form state
  const {
    formData,
    setFormData,
    formErrors,
    setFormErrors,
    fieldValidation,
    isLoading,
    setIsLoading,
    submitError,
    setSubmitError,
    successMessage,
    setSuccessMessage,
    otpSent,
    setOtpSent,
    showPassword,
    showConfirmPassword,
    toggleShowPassword,
    toggleShowConfirmPassword,
    handleChange,
    checkUsernameAvailability,
    checkEmailAvailability,
  } = useSignUpForm();

  // Username blur handler
  const handleUsernameBlur = useCallback(() => {
    if (formData.username) {
      checkUsernameAvailability(formData.username);
    }
  }, [formData.username, checkUsernameAvailability]);

  // Email blur handler
  const handleEmailBlur = useCallback(() => {
    if (formData.email) {
      checkEmailAvailability(formData.email);
    }
  }, [formData.email, checkEmailAvailability]);

  // Handle Get OTP
  const handleGetOtp = createHandleGetOtp(
    formData,
    fieldValidation,
    setFormErrors,
    setIsLoading,
    setSubmitError,
    setOtpSent,
    setSuccessMessage,
    t,
  );

  // Handle form submission
  const handleSubmit = createHandleSubmit(
    formData,
    fieldValidation,
    setFormErrors,
    setIsLoading,
    setSubmitError,
    setSuccessMessage,
    navigate,
  );

  // Handle profile image change
  const handleImageChange = createHandleImageChange(
    setFormData,
    formErrors,
    setFormErrors,
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900">
      <Header
        username={null}
        user_image_url={null}
        logoUrl={APP_CONFIG.LOGO_URL}
      />

      <SectionWrapper>
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className={getPrimaryHeading("hero", true)}>
              {t("signUp.title")}
            </h1>
            <p className={getSupportingText("description")}>
              {t("signUp.subtitle")}
            </p>
          </div>

          {/* Sign Up Form */}
          <div className="bg-gray-800/30 backdrop-blur border border-gray-700/50 rounded-3xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <SignUpFormFields
                formData={formData}
                formErrors={formErrors}
                fieldValidation={fieldValidation}
                isLoading={isLoading}
                otpSent={otpSent}
                showPassword={showPassword}
                showConfirmPassword={showConfirmPassword}
                handleChange={handleChange}
                handleUsernameBlur={handleUsernameBlur}
                handleEmailBlur={handleEmailBlur}
                handleImageChange={handleImageChange}
                toggleShowPassword={toggleShowPassword}
                toggleShowConfirmPassword={toggleShowConfirmPassword}
                handleGetOtp={handleGetOtp}
              />

              <SignUpFormActions
                isLoading={isLoading}
                otpSent={otpSent}
                successMessage={successMessage}
                submitError={submitError}
              />
            </form>
          </div>
        </div>
      </SectionWrapper>

      <Footer logoUrl={APP_CONFIG.LOGO_URL} />
    </div>
  );
};

export default SignUpPage;
